import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import ModalType from '@/Enums/Segments/ModalType.enum';
import type { IParsedSingleSegment, ISegmentFormData } from '@/Interfaces/Segments/Segment.interface';
import CodeSection from '@/components/CodeSection';
import ModalHeaderIcon from '@/components/Icons/ModalHeaderIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { defaultSegment, modalTitle } from '@/constants/segments';
import { createSegment, updateSegment } from '@/store/api/segments.api';
import { useAppSelector } from '@/store/hooks';
import { selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { selectSegmentsAdvertisers, selectSelectedSegment } from '@/store/selectors/segments.selectors';
import { Add } from '@mui/icons-material';
import { Autocomplete, Button, Dialog, DialogContent, InputLabel, TextField } from '@mui/material';
import { HttpStatusCode } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import AssociateCreativesModal from '../CreativesModal';

interface IProps {
  open: boolean;
  onClose: (segment?: Partial<IParsedSingleSegment>) => void;
  modalType: ModalType;
}

const SegmentModal = ({ open, onClose: handleClose, modalType }: IProps) => {
  const [creativesModalOpen, setCreativesModalOpen] = useState<boolean>(false);
  const { selectedSegment, loadingSelectedSegment } = useAppSelector(selectSelectedSegment);

  const advertisers = useAppSelector(selectSegmentsAdvertisers);

  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.SEGMENTS));

  const [loading, setLoading] = useState<boolean>(false);

  //#region form
  const formMethods = useForm<ISegmentFormData>({
    defaultValues: useMemo(() => {
      if (modalType === ModalType.ViewSegment) {
        return selectedSegment;
      }
      return defaultSegment;
    }, [modalType, selectedSegment]),
  });

  const { reset, setValue, handleSubmit, control } = formMethods;

  const creatives = useWatch({ control, name: 'creatives' });
  const advertiser = useWatch({ control, name: 'advertiser' });

  const setCreatives = useCallback(
    (value: IParsedSingleSegment['creatives']) => {
      setValue('creatives', value, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  useEffect(() => {
    reset(selectedSegment);
  }, [reset, selectedSegment]);
  //#endregion

  const onClose = useCallback(() => {
    handleClose();
    reset();
  }, [handleClose, reset]);

  const onDialogClose = useCallback(() => onClose(), [onClose]);

  //#region form save

  const saveNew = useCallback(
    async (data: ISegmentFormData) => {
      setLoading(true);
      try {
        const response = await createSegment(data);
        if (response.status !== HttpStatusCode.Created) {
          throw new Error('Failed to create a Segment');
        }

        const newSegmentAdvertiser = data.advertiser;

        handleClose({
          ...data,
          segmentId: response.data?.segmentId,
          advertiser: newSegmentAdvertiser,
          id: response.data.id,
        });
        toast.success('Segment created successfully');
      } catch (error) {
        console.error(error);
        toast.error('Something went wrong');
      } finally {
        setLoading(false);
      }
      return;
    },
    [reset, handleClose],
  );

  const updateExisting = useCallback(
    async (data: ISegmentFormData) => {
      setLoading(true);
      try {
        await updateSegment(data);

        handleClose(data);
        toast.success('Segment updated successfully');

        reset();
      } catch (error) {
        console.error(error);
        toast.error('Something went wrong');
      } finally {
        setLoading(false);
      }
    },
    [reset, handleClose],
  );

  const onSubmit = useCallback(
    async (data: ISegmentFormData) => {
      if (modalType === ModalType.NewSegment) {
        await saveNew(data);
        return;
      }
      if (modalType === ModalType.ViewSegment) {
        await updateExisting(data);
        return;
      }
    },
    [modalType, saveNew, updateExisting],
  );
  //#endregion

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Dialog maxWidth={false} open={open} onClose={onDialogClose}>
        <DialogContent sx={{ width: '600px', overflowX: 'hidden' }}>
          <ModalTitleCard titleIcon={<ModalHeaderIcon />} handleClose={onClose} title={modalTitle[modalType]} />
          <FormProvider {...formMethods}>
            <form className='mt-4' onSubmit={handleSubmit(onSubmit)}>
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='segment-name'>
                Segment Name
              </InputLabel>
              <Controller
                name='name'
                control={control}
                disabled={readOnly || loadingSelectedSegment}
                rules={{ required: { message: 'Segment Name is required', value: true } }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder='Enter Segment Name'
                    id='segment-name'
                    sx={{ marginBottom: '8px' }}
                    slotProps={{ htmlInput: { maxLength: 99 } }}
                  />
                )}
              />
              <InputLabel sx={{ marginBottom: '4px' }} htmlFor='advertiser-select'>
                Advertiser
              </InputLabel>
              <Controller
                name='advertiser'
                control={control}
                rules={{ required: { message: 'Advertiser is required', value: true } }}
                disabled={readOnly}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id='advertiser-select'
                    sx={{ marginBottom: '8px' }}
                    options={advertisers}
                    fullWidth
                    disabled={modalType === ModalType.ViewSegment}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={option => option?.label || ''}
                    getOptionKey={option => option.value}
                    disableClearable
                    isOptionEqualToValue={(option, selectValue) => selectValue && option.value === selectValue?.value}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                        placeholder='Advertiser'
                        variant='outlined'
                      />
                    )}
                  />
                )}
              />
              <div>
                <InputLabel sx={{ marginBottom: '4px' }} htmlFor='creatives-select'>
                  Creatives ({creatives.length})
                </InputLabel>
                <Button
                  variant='contained'
                  onClick={() => setCreativesModalOpen(true)}
                  disabled={readOnly || loadingSelectedSegment || !advertiser}
                >
                  <Add sx={{ width: 16, height: 16, marginRight: '2px' }} />
                  <span>Add creatives</span>
                </Button>
              </div>
              {modalType !== ModalType.NewSegment ? (
                <CodeSection
                  label='Segment URL'
                  content={selectedSegment.segmentUrl}
                  loading={loadingSelectedSegment}
                />
              ) : null}
              <hr className='-mx-[32px] my-5' />
              <div className='mt-5 flex gap-2 items-center justify-end'>
                <Button variant='outlined' onClick={onClose}>
                  Close
                </Button>
                <Button variant='contained' type='submit' disabled={readOnly}>
                  Save
                </Button>
              </div>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
      <AssociateCreativesModal
        open={creativesModalOpen}
        onClose={() => setCreativesModalOpen(false)}
        associatedCreatives={creatives}
        onAssociateCreatives={setCreatives}
        advertiser={advertiser}
      />
    </LoadingBackdropWrapper>
  );
};

export default SegmentModal;
